import React from "react";
import {
  Box,
  Paper,
  Chip,
  IconButton,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { PromptTemplateDto } from "@/dto/PromptTemplateDto";
import { ResultListDto } from "@/dto/ResponseDto";
import { PromptTemplate } from "@/organism";

interface ListTemplateProps {
  data: ResultListDto<PromptTemplateDto> | null;
  loading: boolean;
  categories: string[];
  selectedCategory: string;
  searchKeyword: string;
  onCategoryChange: (category: string) => void;
  onSearch: (keyword: string) => void;
  onDetail: (templateId: number) => void;
  onDelete: (templateId: number) => void;
  onPageChange?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

export default function ListTemplate({
  data,
  loading,
  categories,
  selectedCategory,
  searchKeyword,
  onCategoryChange,
  onSearch,
  onDetail,
  onDelete,
  onPageChange,
  onRowsPerPageChange,
}: ListTemplateProps) {
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* 검색 및 필터 */}
      <PromptTemplate.Filter
        values={{
          category: selectedCategory,
          keyword: searchKeyword,
        }}
        categories={categories}
        onChange={(values) => {
          onCategoryChange(values.category);
          onSearch(values.keyword);
        }}
        onReset={() => {
          onCategoryChange("");
          onSearch("");
        }}
      />

      {/* 테이블 */}
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="right" sx={{ width: 80 }}>
                      ID
                    </TableCell>
                    <TableCell sx={{ width: 200 }}>Template Key</TableCell>
                    <TableCell sx={{ width: 250 }}>Template Name</TableCell>
                    <TableCell align="center" sx={{ width: 120 }}>
                      Category
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>Provider</TableCell>
                    <TableCell sx={{ width: 120 }}>Model</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>
                      Temperature
                    </TableCell>
                    <TableCell align="center" sx={{ width: 80 }}>
                      Priority
                    </TableCell>
                    <TableCell align="center" sx={{ width: 80 }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{ width: 120 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.list && data.list.length > 0 ? (
                    data.list.map((row) => (
                      <TableRow key={row.templateId} hover>
                        <TableCell align="right">{row.templateId}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", cursor: "pointer" }}
                            onClick={() => onDetail(row.templateId)}
                          >
                            {row.templateKey}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ cursor: "pointer" }}
                            onClick={() => onDetail(row.templateId)}
                          >
                            {row.templateName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={row.templateCategory} size="small" />
                        </TableCell>
                        <TableCell>{row.aiModelProvider}</TableCell>
                        <TableCell>{row.aiModelName}</TableCell>
                        <TableCell align="right">
                          {row.aiModelTemperature}
                        </TableCell>
                        <TableCell align="center">
                          {row.priorityOrder}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.isActive ? "Active" : "Inactive"}
                            size="small"
                            color={row.isActive ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => onDetail(row.templateId)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => onDelete(row.templateId)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No templates found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {data && data.count > 0 && (
              <TablePagination
                component="div"
                count={data.count}
                page={(data.page || 1) - 1}
                onPageChange={onPageChange || (() => {})}
                rowsPerPage={data.limit || 20}
                onRowsPerPageChange={onRowsPerPageChange || (() => {})}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
              />
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
